import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

type DraftContext = 'narrative' | 'meddra' | 'duplicate' | 'literature' | 'signal' | 'general';

const CONTEXT_TEXT: Record<DraftContext, string> = {
  narrative:
    'Draft only. Requires qualified human review and e-signature before regulatory use.',
  meddra:
    'Draft only. AI suggestion -- requires qualified medical coder verification before regulatory use.',
  duplicate:
    'Draft only. Suspected-duplicate flag from AI -- requires PV physician confirmation.',
  literature:
    'Draft only. AI literature triage -- requires qualified PV reviewer before any regulatory action.',
  signal:
    'Draft only. AI signal-detection output -- requires qualified safety physician review before regulatory action.',
  general:
    'Draft only. Requires qualified human review before regulatory use.',
};

interface DraftOnlyLabelProps {
  variant?: 'block' | 'inline';
  context?: DraftContext;
  className?: string;
}

export function DraftOnlyLabel({
  variant = 'block',
  context = 'general',
  className,
}: DraftOnlyLabelProps) {
  const text = CONTEXT_TEXT[context];

  if (variant === 'inline') {
    return (
      <span
        role="note"
        aria-label="AI output safety notice"
        data-testid="draft-only-label"
        className={cn(
          'inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400',
          className,
        )}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        {text}
      </span>
    );
  }

  return (
    <div
      role="note"
      aria-label="AI output safety notice"
      data-testid="draft-only-label"
      className={cn(
        'flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}
